import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../Components/UI/card';
import { Button } from '../../../Components/UI/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../Components/UI/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../Components/UI/tooltip';
import {
  Plus,
  Check,
  X,
  Building2,
  Clock,
  CheckCircle,
  ListCheck,
  User,
  PhoneCall,
  RefreshCcw
} from 'lucide-react';
import { getTodayTimeDisplayText, currentUserHasPermission, canEditTask, getCurrentUser, getTaskCategoryIDForUser, getMyAgenciesMappedIDs, playTaskSuccessSound } from '../../../Utils/Utilities';
import RecordEntry from '../../Records/RecordsEntry';
import { toast } from 'sonner';
import { cancelTaskAPI, rejectTaskAPI, completeTaskAPI } from '../../Tasks/TasksAPI';
import { useDesktop } from '../../../Components/Common/IsDesktop';
import ThrDetails from '../../Threads/ThrDetails';
import ThreadEntry from '../../Threads/ThreadEntry';
import { useSelector } from 'react-redux';
import { checkIfInqOnlyMode, getUserConfig } from '../../../Store/Reducers';
import { getAuthData } from '../../../Utils/AuthUtils';
import ThreadDropdown from '../../Threads/AddThreadDropdown';
import TaskDetails from '../../Tasks/TaskDetails';
import { TaskCompleteDialog } from '../../../Components/Common/TaskCompleteDialog';
import { getPriorityDetails } from '../../../Utils/Constants';
import { handlePhoneClickAction } from '../../../Utils/ContactLogUtils';
import { MdPhone } from 'react-icons/md';
import taskClassConfig from '../../../Config/taskClassConfig.json';

const Tasks = ({
  processedData = {
    todayTasks: [],
    overdueTasks: [],
    thisWeekTasks: [],
  },
  isMobile = false,
  onTaskEntryOpen,
  onRepeatTaskEntryOpen,
  onRecordAdded,
  showInquiries = true
}) => {
  const [confirmCompleteDialog, setConfirmCompleteDialog] = useState(false);
  const [confirmCancelDialog, setConfirmCancelDialog] = useState(false);
  const [recordEntryDialog, setRecordEntryDialog] = useState(false);
  const [selectedTaskForRecord, setSelectedTaskForRecord] = useState(null);
  const [isThreadDetailsOpen, setIsThreadDetailsOpen] = useState(false);
  const [selectedThreadID, setSelectedThreadID] = useState(null);
  const [threadEntryOpen, setThreadEntryOpen] = useState(false);
  const [isTaskInqDDOpen, setIsTaskInqDDOpen] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const pendingRepeatAfterRecordRef = useRef(false);
  const pendingStatusNoteAfterRecordRef = useRef("");
  const { data: threadData } = useSelector(state => state.threadData);
  const { data: connectionData } = useSelector((state) => state.connectionData);
  const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
  const threadTypeStr = isInqOnlyMode ? 'Inquiry' : 'Sales Inquiry';
  const isDesktop = useDesktop();
  const authData = getAuthData();
  const currentUserID = authData?.UserID;
  const [taskDetailsOpen, setTaskDetailsOpen] = useState(false);
  const thrActions = currentUserHasPermission('ThrActions');
  const canAddThr = thrActions >= 1 && thrActions <= 3;
  const currentUser = getCurrentUser();
  // Task categories now come directly from the agency Config ('C') data instead of the per-user Users ('U') data.
  const myTaskCtgs = (() => {
    try { return JSON.parse(getUserConfig()?.TaskCtgs || '[]'); }
    catch { return []; }
  })();

  // Scroll position tracking refs
  const scrollPositionRef = useRef(0);
  const scrollableContainerRef = useRef(null);

  // Helper function to get threads needing closure on specific dates
  const getThreadsNeedingClosure = () => {
    if (!showInquiries || !threadData?.length) return [];
    return threadData.filter(thread => {
      // Check if thread belongs to a cold contact
      const threadConnection = connectionData.find(c => c.ConnID === thread.ConnID);
      if (threadConnection && (threadConnection.IsCold === 1 || threadConnection.IsCold === true)) {
        return false;
      }
      return thread.CurrStageID != 7 && thread.TypeID == 1 && (thread.POCUserID == currentUserID || thread.CreatedUserID == currentUserID)
    });
  };

  // Helper function to categorize threads by due date
  const categorizeThreadsByDueDate = () => {
    const threads = getThreadsNeedingClosure();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const overdue = [];
    const dueToday = [];
    const dueThisWeek = [];

    threads.forEach(thread => {
      const targetDate = new Date(thread.DueDT);
      targetDate.setHours(0, 0, 0, 0);

      if (targetDate < today) {
        overdue.push(thread);
      } else if (targetDate.getTime() === today.getTime()) {
        dueToday.push(thread);
      } else {
        const daysUntilDue = Math.floor((targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        if (daysUntilDue <= 7) {
          dueThisWeek.push(thread);
        }
      }
    });

    return { overdue, dueToday, dueThisWeek };
  };

  const threadsByDate = categorizeThreadsByDueDate();

  // Function to determine the initial active tab based on available tasks
  const getInitialActiveTab = () => {
    const hasToday =
      processedData.todayTasks.length > 0 ||
      threadsByDate.dueToday.length > 0;

    const hasOverdue =
      processedData.overdueTasks.length > 0 ||
      threadsByDate.overdue?.length > 0;

    const hasThisWeek =
      processedData.thisWeekTasks.length > 0 ||
      threadsByDate.dueThisWeek?.length > 0;

    if (hasToday) return "today";
    if (hasOverdue) return "overdue";
    if (hasThisWeek) return "this-week";

    // All empty → default
    return "today";
  };

  const [activeTab, setActiveTab] = useState(getInitialActiveTab());

  // Update active tab when processedData changes
  useEffect(() => {
    // Only auto-switch tabs if user hasn't manually selected a tab
    if (!hasUserInteracted) {
      const newActiveTab = getInitialActiveTab();
      setActiveTab(newActiveTab);
    }
  }, [
    processedData.todayTasks.length,
    processedData.overdueTasks.length,
    processedData.thisWeekTasks.length,
    threadsByDate.dueToday.length,
    threadsByDate.overdue.length,
    threadsByDate.dueThisWeek.length,
    hasUserInteracted
  ]);

  // Effect to restore scroll position when tab content changes
  useEffect(() => {
    if (scrollableContainerRef.current && scrollPositionRef.current > 0) {
      scrollableContainerRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [activeTab]);

  // Restore scroll position immediately when any dialog state changes
  useEffect(() => {
    if (scrollableContainerRef.current) {
      requestAnimationFrame(() => {
        if (scrollableContainerRef.current) {
          scrollableContainerRef.current.scrollTop = scrollPositionRef.current;
        }
      });
    }
  }, [confirmCompleteDialog, confirmCancelDialog, recordEntryDialog, isThreadDetailsOpen]);

  // Real-time scroll position tracking - maintains scroll position throughout modal interactions
  useEffect(() => {
    const scrollableDiv = scrollableContainerRef.current;
    if (scrollableDiv) {
      const handleScroll = () => {
        scrollPositionRef.current = scrollableDiv.scrollTop;
      };
      scrollableDiv.addEventListener('scroll', handleScroll);
      return () => scrollableDiv.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Helper function to sort tasks: High Priority first, then Normal, then Low, then chronological ASC
  const sortItems = (items) => {
    const getPriWeight = (item) => {
      if (item.itemType === 'task') {
        const pri = item.Pri;
        if (pri === 'H') return 3;
        if (pri === 'L') return 1;
      }
      return 2; // Normal/unassigned priority for tasks and all threads
    };

    return [...items].sort((a, b) => {
      const weightA = getPriWeight(a);
      const weightB = getPriWeight(b);
      if (weightA !== weightB) return weightB - weightA;

      const getTimestamp = (item) => {
        const val = item.itemType === 'task' ? item.DT : item.DueDT;
        if (!val) return Infinity;
        const t = new Date(val).getTime();
        return isNaN(t) ? Infinity : t;
      };

      return getTimestamp(a) - getTimestamp(b);
    });
  };

  // Tab configuration - Tasks
  const taskTabs = [
    {
      value: 'overdue',
      label: 'Overdue',
      mobileLabel: 'Overdue',
      desktopLabel: 'Overdue',
      data: sortItems([
        ...processedData.overdueTasks.map(task => ({ ...task, itemType: 'task' })),
        ...threadsByDate.overdue.map(thread => ({ ...thread, itemType: 'thread' }))
      ]),
      emptyMessage: 'No overdue tasks or inquiries.',
      showDateTime: true,
    },
    {
      value: 'today',
      label: 'Today',
      mobileLabel: 'Today',
      desktopLabel: 'Today',
      data: sortItems([
        ...processedData.todayTasks.map(task => ({ ...task, itemType: 'task' })),
        ...threadsByDate.dueToday.map(thread => ({ ...thread, itemType: 'thread' }))
      ]),
      emptyMessage: 'No tasks or inquiries for today.',
      showDateTime: false,
    },
    {
      value: 'this-week',
      label: 'Week Ahead',
      mobileLabel: 'Next 7 days',
      desktopLabel: 'Week Ahead',
      data: sortItems([
        ...processedData.thisWeekTasks.map(task => ({ ...task, itemType: 'task' })),
        ...threadsByDate.dueThisWeek.map(thread => ({ ...thread, itemType: 'thread' }))
      ]),
      emptyMessage: 'No tasks or inquiries for this week.',
      showDateTime: true,
    }
  ];

  // All tabs
  const tabs = taskTabs;

  // Get current tab data
  const getCurrentTabData = () => tabs.find(tab => tab.value === activeTab);

  // Calculate total tasks for current active tab
  const getTotalTasksForActiveTab = () => getCurrentTabData()?.data.length || 0;

  // Variable which count total number of task is less than 5 or not
  const shouldShowAddButton = getTotalTasksForActiveTab() <= 5;

  const formatTaskData = (task) => {
    const hasValidDate = task.DT && !isNaN(new Date(task.DT).getTime());
    const dateObj = hasValidDate ? new Date(task.DT) : null;
    const formattedTask = {
      taskID: task.TskID,
      connID: task.ConnID,
      taskName: task.Ttl,
      description: task.Txt,
      dueDT: dateObj ? dateObj.toISOString().split('T')[0] : '',
      dueTime: dateObj ? dateObj.toTimeString().slice(0, 5) : '',
      pocUserID: task.UID,
      priority: task.Pri,
      threads: (task.TIDs && typeof task.TIDs === 'string') ? task.TIDs.split(',') : '',
      status: task.Sts,
      taskType: task.Typ,
    }
    return formattedTask;
  }

  // Function to handle thread click
  const handleThreadClick = (thread, e) => {
    e.stopPropagation();
    // Save scroll position before opening dialog
    if (scrollableContainerRef.current) {
      scrollPositionRef.current = scrollableContainerRef.current.scrollTop;
    }
    setSelectedThreadID(thread.ThreadID);
    setIsThreadDetailsOpen(true);
  };

  // Function which will direct complete the task
  const handleDirectComplete = async (task, repeatTask = false, statusNote = "") => {
    const taskId = task.TskID;

    try {
      const taskData = {
        taskID: taskId,
        connID: task.ConnID,
        pocUserID: task.UID,
        taskName: task.Ttl,
        description: task.Txt,
        priority: task.Pri,
        status: task.Sts,
        taskType: task.Typ,
        threads: task.TIDs,
        AgencyID: task.AgencyID,
        repeatTask: repeatTask,
        statusNote: statusNote
      };

      const taskClass = Number(task.Class || task.class || task.TaskClass || task.taskClass || 1);
      const classLabel = taskClass === 2 ? "Meeting" : taskClass === 3 ? "Decision" : taskClass === 4 ? "Approval" : "Task";
      const result = await completeTaskAPI(taskData);
      if (result.message == 'Success' && result.error == false) {
        // playTaskSuccessSound();
        toast.success(`${classLabel} completed.`);
      }

    } catch (error) {
      console.error('Error completing task:', error);
      const taskClass = Number(task.Class || task.class || task.TaskClass || task.taskClass || 1);
      const classLabel = taskClass === 2 ? "Meeting" : taskClass === 3 ? "Decision" : taskClass === 4 ? "Approval" : "Task";
      toast.error(error.message || `Failed to complete ${classLabel.toLowerCase()}. Please try again.`);
    } finally {
      setConfirmCompleteDialog(false);
      setSelectedTaskForRecord(null);
    }
  };

  // Function which will direct cancel the task
  const handleDirectCancelTask = async (task, statusNote = "") => {
    const taskId = task.TskID;

    try {
      const taskData = {
        taskID: taskId,
        connID: task.ConnID,
        pocUserID: task.UID,
        taskName: task.Ttl,
        description: task.Txt,
        priority: task.Pri,
        status: task.Sts,
        taskType: task.Typ,
        threads: task.TIDs,
        AgencyID: task.AgencyID,
        statusNote: statusNote
      };

      // Assignee (not creator) declining the task is a Reject, not a Cancel
      const myUserIDs = getMyAgenciesMappedIDs('UserID');
      const taskCreatorID = Number(task.CreatedUID || task.createdUserID);
      const taskAssigneeID = Number(task.UID || task.pocUserID || task.uID);
      const isMyCreator = myUserIDs.includes(taskCreatorID) || taskCreatorID === currentUserID;
      const isMyAssignee = myUserIDs.includes(taskAssigneeID) || taskAssigneeID === currentUserID;
      const isReject = isMyAssignee && !isMyCreator;

      const taskClass = Number(task.Class || task.class || task.TaskClass || task.taskClass || 1);
      const classLabel = taskClass === 2 ? "Meeting" : taskClass === 3 ? "Decision" : taskClass === 4 ? "Approval" : "Task";

      const result = isReject ? await rejectTaskAPI(taskData) : await cancelTaskAPI(taskData);
      if (result.message == 'Success' && result.error == false) {
        toast.success(isReject ? `${classLabel} rejected.` : `${classLabel} cancelled.`);
      }

    } catch (error) {
      console.error('Error cancelling task:', error);
      const taskClass = Number(task.Class || task.class || task.TaskClass || task.taskClass || 1);
      const classLabel = taskClass === 2 ? "Meeting" : taskClass === 3 ? "Decision" : taskClass === 4 ? "Approval" : "Task";
      toast.error(error.message || `Failed to cancel ${classLabel.toLowerCase()}. Please try again.`);
    } finally {
    }
  };

  // Function which called when user want to enter record
  const handleRecordAndComplete = () => {
    setConfirmCompleteDialog(false);
    setRecordEntryDialog(true);
  };

  // Function which is used to save record
  const handleRecordSaved = async () => {
    setRecordEntryDialog(false);
    if (onRecordAdded) {
      await onRecordAdded();
    }
    pendingRepeatAfterRecordRef.current = false;
  };

  // Function which will handle complete task
  const handleCompleteTask = async (task, calledFor = '') => {
    // Save scroll position before opening dialog
    if (scrollableContainerRef.current) {
      scrollPositionRef.current = scrollableContainerRef.current.scrollTop;
    }

    setSelectedTaskForRecord(task);
    if (calledFor === 'taskDetails') {
      setTaskDetailsOpen(true)
    } else {
      setConfirmCompleteDialog(true);
    }
  };

  // Function which will handle cancel task
  const handleCancelTask = (task) => {
    // Save scroll position before opening dialog
    if (scrollableContainerRef.current) {
      scrollPositionRef.current = scrollableContainerRef.current.scrollTop;
    }
    setSelectedTaskForRecord(task);
    setConfirmCancelDialog(true);
  }

  // Reusable Empty State Component
  const EmptyState = ({ message, isMobile }) => (
    <div className="text-center py-8">
      <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center bg-gray-100">
        <CheckCircle className="text-sahi-green" size={24} />
      </div>
      <h4 className={`font-semibold ${isMobile ? 'text-sm' : 'text-lg'} text-sahi-primary-color`}>
        {"All caught up!"}
      </h4>
      <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-sahi-grey`}>
        {message}
      </p>
    </div>
  );

  // Reusable Task Action Buttons Component
  const TaskActionButtons = ({ task, isMobile }) => {
    const myUserIDs = getMyAgenciesMappedIDs('UserID');
    const isMyCreator = myUserIDs.includes(Number(task.CreatedUID || task.createdUserID)) || Number(task.CreatedUID || task.createdUserID) === currentUserID;
    const isMyAssignee = myUserIDs.includes(Number(task.UID || task.pocUserID || task.uID)) || Number(task.UID || task.pocUserID || task.uID) === currentUserID;
    const isCreatorOrAdminBase = isMyCreator;
    const isAssigneeOnly = isMyAssignee && !isCreatorOrAdminBase;

    const isOpen = Number(task.Sts || task.sts || task.status || 1) === 1;
    const isTaskClosed = Number(task.Sts || task.sts || task.status) === 2 || Number(task.Sts || task.sts || task.status) === 4 || Number(task.Sts || task.sts || task.status) === 0 || Number(task.Sts || task.sts || task.status) === 3;
    const isCreatorOrAdmin = isMyCreator && !isTaskClosed;

    const canCancel = isCreatorOrAdmin;
    const canComplete = (!isTaskClosed && isCreatorOrAdmin) || (isAssigneeOnly && isOpen && !isTaskClosed && (task.CanComplete == true || task.canComplete == true));

    return (
      <div className="flex items-center gap-3">
        {(() => {
          const connection = connectionData.find(c => c.ConnID === task.ConnID);
          return connection && connection.Mobile !== "" && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} text-black hover:bg-gray-500`}
                  onClick={(e) => {
                    e.stopPropagation();
                    const isDesktop =
                      typeof window !== "undefined" && window.innerWidth > 768;
                    handlePhoneClickAction(
                      connection,
                      isDesktop,
                      "",
                      task.ConnID
                    );
                  }}
                >
                  <MdPhone className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
                </Button>
              </TooltipTrigger>

              <TooltipContent>
                <p>{connection.Mobile}</p>
              </TooltipContent>
            </Tooltip>
          );
        })()}

        {canComplete && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} text-sahi-green hover:bg-sahi-green-light`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCompleteTask(task);
                }}
              >
                <Check className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Mark as complete
            </TooltipContent>
          </Tooltip>
        )}
        {canCancel && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} text-sahi-red hover:bg-sahi-red-light`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelTask(task);
                }}
              >
                <X className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Cancel task
            </TooltipContent>
          </Tooltip>
        )}
        {!canCancel && isAssigneeOnly && isOpen && !isTaskClosed && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} text-sahi-red hover:bg-sahi-red-light`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelTask(task);
                }}
              >
                <X className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Reject task
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    );
  };

  // Reusable Task Card Component
  const TaskCard = ({ item, isMobile, showDateTime, length }) => (
    <Card
      className={`${isMobile ? 'p-3' : 'p-4'} shadow-sm bg-sahi-disable gap-0 hover:cursor-pointer`}
      onClick={(e) => {
        e.stopPropagation();
        handleCompleteTask(item, 'taskDetails');
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-foreground flex items-center gap-1.5`}>
          <span>{(() => {
            const classId = Number(item.Class || item.class || item.TaskClass || item.taskClass || 1);
            const matchedClass = taskClassConfig?.taskClasses?.find(c => c.id === classId);
            return matchedClass ? matchedClass.label : "Task";
          })()}</span>
          {item.Pri === 'H' && (() => {
            const priority = getPriorityDetails(item.Pri);
            const IconComponent = priority.icon;
            return (
              <span style={{ color: priority.color }} title={`${priority.label} Priority`}>
                <IconComponent className="h-3.5 w-3.5" strokeWidth={2.5} />
              </span>
            );
          })()}
        </span>
        <TaskActionButtons task={item} isMobile={isMobile} />
      </div>
      <h4 className={`${isMobile ? 'text-sm' : 'text-base'} leading-tight text-foreground mb-2 flex items-start gap-1.5`}>
        {!isMobile && (() => {
          const usrCID = getTaskCategoryIDForUser(item, currentUserID) || null;
          const cat = usrCID ? myTaskCtgs.find(c => c.CID === usrCID && !c.IsD) : null;
          if (!cat) return null;
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 cursor-pointer translate-y-[5px]" style={{ backgroundColor: cat.Clr }} />
              </TooltipTrigger>
              <TooltipContent>
                <p>{cat.CNm}</p>
              </TooltipContent>
            </Tooltip>
          );
        })()}
        {(Number(item.RepeatAfter || item.repeatAfter) > 0 || item.RepeatDT || item.repeatDT) && (
          <RefreshCcw className="h-3 w-3 text-zinc-500 shrink-0 mt-[3px]" />
        )}
        <span className="flex-1">{item.Ttl || "Untitled Task"}</span>
      </h4>
      {/* <h4 className={`text-sm text-foreground font-medium mb-1 leading-tight ${
						length > 10
						? "line-clamp-1"
						: "break-words"
					}`}>
		{item.Ttl || "Untitled Task"}
	</h4> */}
      {(item.BizName == "" && item.ContactName == "") == false ? <div className="flex items-center gap-2 mb-2 text-sahi-primary-color">
        {item.BizName != '' ? <Building2 size={14} /> : item.ContactName ? <User size={14} /> : ''}
        <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold`}>
          {item.BizName || item.ContactName || '-'}
        </span>
      </div> : null}
      {showDateTime && (
        <div className="flex items-center gap-2 text-xs text-sahi-primary-color">
          <Clock size={12} />
          {getTodayTimeDisplayText(item.DT, true).replace("11:59 PM", "by EOD")}
        </div>
      )}
    </Card>
  );

  const ThreadCard = ({ item, isMobile, showDateTime }) => (
    <Card
      className={`${isMobile ? 'p-3' : 'p-4'} shadow-sm bg-sahi-disable gap-0 hover:cursor-pointer hover:bg-gray-50 transition-colors`}
      onClick={(e) => handleThreadClick(item, e)}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-foreground`}>
          {`${threadTypeStr} #${item.ThreadID}`} (Closure)
        </span>
      </div>
      {item.connectionData && (
        <div className="flex !items-center gap-2 my-1.5 text-sahi-primary-color">
          <Building2 size={14} />
          <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold`}>
            {item.connectionData?.BizName || item.connectionData?.ContactName || '-'}
          </span>
        </div>
      )}
      <h4 className={`${isMobile ? 'text-sm' : 'text-base'} leading-tight text-foreground ${showDateTime && 'mb-2'}`}>
        {item.MainProduct ? item.MainProduct : ''}
      </h4>
      {showDateTime && item.DueDT && (
        <div className="flex items-center gap-2 text-xs text-sahi-primary-color">
          <Clock size={12} />
          {getTodayTimeDisplayText(item.DueDT, true).replace("11:59 PM", "by EOD")}
        </div>
      )}
    </Card>
  );

  // Reusable Tab Content Component
  const TabContentWrapper = ({ tabData, isMobile }) => (
    <div className="space-y-4">
      {tabData.data.length === 0 ? (
        <EmptyState message={tabData.emptyMessage} isMobile={isMobile} />
      ) : (
        tabData.data.map((item, idx) =>
          item.itemType === 'thread' ? (
            <ThreadCard
              key={`thread-${item.ThreadID}-${idx}`}
              item={item}
              isMobile={isMobile}
              showDateTime={tabData.showDateTime}
            />
          ) : (
            <TaskCard
              key={`task-${item.TskID}-${idx}`}
              item={item}
              isMobile={isMobile}
              showDateTime={tabData.showDateTime}
              length={tabData.data.length}
            />
          )
        )
      )}
    </div>
  );

  // Reusable Add Task Button Component
  const AddTaskButton = ({ isMobile }) => (
    <Button
      variant="secondary"
      className={`${isMobile ? 'mt-4' : ''} flex items-center gap-2 p-2 px-2 border border-sahi-border-color bg-sahi-disable shadow-sm hover:bg-sahi-blue-light hover:text-sahi-primary-color hover:border-transparent`}
      onClick={onTaskEntryOpen}
    >
      <Plus className="h-4 w-4" />
      Add Task
    </Button>
  );

  // Reusable Add Inquiry Button Component
  const AddInquiryButton = ({ isMobile }) => (
    canAddThr && (
      <Button
        variant="secondary"
        className={`${isMobile ? 'mt-4' : ''} flex items-center gap-2 p-2 px-2 border border-sahi-border-color bg-sahi-disable shadow-sm hover:bg-sahi-blue-light hover:text-sahi-primary-color hover:border-transparent`}
        onClick={() => setThreadEntryOpen(true)}
      >
        <Plus className="h-4 w-4" />
        Add Inquiry
      </Button>
    )
  );

  // Mobile Component
  const MobileTasksCard = () => (
    <Card className="lg:hidden border-none !rounded-xl bg-sahi-white">
      <CardHeader className="pt-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <span className="text-text-color text-xl">Tasks</span>
          <div
            className="text-sahi-primary-color hover:cursor-pointer hover:bg-sahi-blue-light sahi-radius p-1 transition-colors ml-auto"
            onClick={onTaskEntryOpen}
            title="Add Task"
          >
            <Plus className="h-6 w-6" />
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-3">
        <Tabs value={activeTab} onValueChange={(value) => {
          setActiveTab(value);
          setHasUserInteracted(true);
        }} className="w-full">
          <TabsList className="grid w-full grid-cols-3 !rounded-md border shadow-sm">
            {tabs.map(tab => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="!rounded-md text-xs "
              >
                {/* {tab.mobileLabel}{tab.data.length ? ` (${tab.data.length})` : ""} */}
                {tab.mobileLabel}
              </TabsTrigger>
            ))}
          </TabsList>

          {tabs.map(tab => (
            <TabsContent key={tab.value} value={tab.value} className="mt-4 ">
              <TabContentWrapper tabData={tab} isMobile={true} />
            </TabsContent>
          ))}

          {shouldShowAddButton && (
            <div className='flex gap-2 items-center'>
              <AddTaskButton isMobile={true} />
              <AddInquiryButton isMobile={true} />
            </div>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );

  // Desktop Component
  const DesktopTasksPanel = () => (
    <div
      className="w-[445px] hidden lg:flex flex-col"
      style={{
        backgroundColor: "var(--sahi-white)",
        height: "100vh",
      }}
    >
      <div className="flex flex-col h-full">
        {/* Header - Fixed */}
        <div className="p-6 pb-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center justify-center">
              {/* <ListCheck className="text-sahi-blue mr-1" /> */}
              <h2 className="text-lg text-foreground font-semibold">Tasks</h2>
            </div>
            <div
              className="text-sahi-primary-color hover:cursor-pointer hover:bg-sahi-blue-light sahi-radius p-1 transition-colors"
              onClick={onTaskEntryOpen}
              title="Add Task"
            >
              <Plus className="h-6 w-6" />
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={(value) => {
            setActiveTab(value);
            setHasUserInteracted(true);
          }} className="w-full">
            <TabsList className="flex w-full gap-1">
              {tabs.map(tab => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="flex-1 sahi-radius"
                >
                  {tab.desktopLabel}{tab.data.length ? ` (${tab.data.length > 99 ? "99+" : tab.data.length})` : ""}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Scrollable Content Area */}
        <div
          className="flex-1 overflow-y-auto px-6"
          ref={scrollableContainerRef}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {tabs.map(tab => (
              <TabsContent key={tab.value} value={tab.value} className="mt-0">
                <div className="space-y-4 py-2">
                  <TabContentWrapper tabData={tab} isMobile={false} />
                  {shouldShowAddButton && (
                    <div className='flex gap-2 items-center'>
                      <AddTaskButton isMobile={false} />
                      <AddInquiryButton isMobile={false} />
                    </div>
                  )}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </div>
  );

  return (
    <TooltipProvider>
      {isMobile ? <MobileTasksCard /> : <DesktopTasksPanel />}

      {/* Task complete alert box */}
      <TaskCompleteDialog
        open={confirmCompleteDialog}
        onOpenChange={setConfirmCompleteDialog}
        task={selectedTaskForRecord}
        onAction={(val, repeat, statusNote) => {
          if (val === "complete") {
            handleDirectComplete(selectedTaskForRecord, repeat, statusNote)
          } else if (val === "record") {
            pendingRepeatAfterRecordRef.current = repeat;
            pendingStatusNoteAfterRecordRef.current = statusNote;
            handleRecordAndComplete();
          } else {
            setConfirmCompleteDialog(false);
          }
        }}
      />

      {/* cancel/reject alert box */}
      <TaskCompleteDialog
        mode={(() => {
          if (!selectedTaskForRecord) return "cancel";
          const myUserIDs = getMyAgenciesMappedIDs('UserID');
          const taskCreatorID = Number(selectedTaskForRecord.CreatedUID || selectedTaskForRecord.createdUserID);
          const taskAssigneeID = Number(selectedTaskForRecord.UID || selectedTaskForRecord.pocUserID || selectedTaskForRecord.uID);
          const isMyCreator = myUserIDs.includes(taskCreatorID) || taskCreatorID === currentUserID;
          const isMyAssignee = myUserIDs.includes(taskAssigneeID) || taskAssigneeID === currentUserID;
          return (isMyAssignee && !isMyCreator) ? "reject" : "cancel";
        })()}
        open={confirmCancelDialog}
        onOpenChange={setConfirmCancelDialog}
        task={selectedTaskForRecord}
        className={isDesktop ? "w-[400px]" : ""}
        onAction={(val, statusNote) => {
          if (val === "yes") {
            setConfirmCancelDialog(false);
            handleDirectCancelTask(selectedTaskForRecord, statusNote);
          } else {
            setConfirmCancelDialog(false);
          }
        }}
      />

      {/* RecordEntry dialog */}
      <RecordEntry
        triggerButton={false}
        open={recordEntryDialog}
        onOpenChange={setRecordEntryDialog}
        prefilledData={{
          connID: selectedTaskForRecord?.ConnID,
          connectionLocked: true,
          bizName: selectedTaskForRecord?.BizName || '',
          threadIDs: (selectedTaskForRecord && typeof selectedTaskForRecord.TIDs === 'string') ? selectedTaskForRecord.TIDs.split(",") : [],
        }}
        selectedTaskForRecord={selectedTaskForRecord ? { ...formatTaskData(selectedTaskForRecord), repeatTask: pendingRepeatAfterRecordRef.current, statusNote: pendingStatusNoteAfterRecordRef.current } : null}
        onRecordAdded={handleRecordSaved}
      />

      {/* Thread Details */}
      <ThrDetails
        isOpen={isThreadDetailsOpen}
        onClose={() => setIsThreadDetailsOpen(false)}
        threadID={selectedThreadID}
      />

      {/* Thread Entry */}
      <ThreadEntry
        triggerButton={false}
        threadTypeID={1}
        open={threadEntryOpen}
        onOpenChange={setThreadEntryOpen}
        onThreadUpdated={() => {
          // Refresh logic if needed
        }}
      />

      <TaskDetails
        isOpen={taskDetailsOpen}
        onClose={() => setTaskDetailsOpen(false)}
        taskID={selectedTaskForRecord?.TskID}
      />
    </TooltipProvider>
  );
};

export default Tasks;